import { useMemo, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { getScreenKey } from "../constants/liaNotifications";

/**
 * Paleta do degradê `agent-facilities-pill` (tailwind.config.js).
 * Mantida em sincronia manualmente — se a paleta mudar lá, atualizar aqui.
 */
const FACILITIES_GRADIENT_PALETTE = [
  "#A768FF",
  "#7C3AED",
  "#343CED",
  "#4F46E5",
  "#EC4899",
] as const;

/** Posições de parada do degradê original, preservadas para manter o "peso" visual. */
const STOP_POSITIONS = [0, 22, 55, 70, 100] as const;

function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export interface RandomFacilitiesGradient {
  /** Muda a cada tela — use como `key` para reiniciar a animação de entrada. */
  gradientKey: string;
  /** Estilos inline do degradê (backgroundImage + backgroundSize para animação). */
  style: CSSProperties;
}

/**
 * Gera um degradê aleatório com a paleta do Agente de Facilities.
 * Um novo sorteio (ordem das cores + ângulo) acontece no primeiro carregamento
 * e sempre que o usuário troca de tela (screenKey derivado do pathname).
 */
export function useRandomFacilitiesGradient(): RandomFacilitiesGradient {
  const { pathname } = useLocation();
  const screenKey = getScreenKey(pathname);

  return useMemo(() => {
    const colors = shuffle(FACILITIES_GRADIENT_PALETTE);
    const angle = Math.floor(Math.random() * 360);
    const stops = colors
      .map((color, i) => `${color} ${STOP_POSITIONS[i]}%`)
      .join(", ");

    return {
      gradientKey: `${screenKey}-${angle}`,
      style: {
        backgroundImage: `linear-gradient(${angle}deg, ${stops})`,
        backgroundSize: "200% 200%",
      },
    };
  }, [screenKey]);
}
